import type { FC } from 'react';
import React, { useState, useEffect, useRef } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';
import { B100 } from '@atlaskit/theme/colors';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const DragHandle = styled.div<{ isDown: boolean }>`
	width: 10px;
	height: 100%;
	top: 0;
	position: absolute;
	cursor: col-resize;
	border-left: 2px solid
		${(props) => (props.isDown ? token('color.border.brand', B100) : 'transparent')};
	margin-left: ${token('space.negative.150', '-12px')};
	transition: border-color 0.2s ease-in-out;
	&:hover {
		border-color: ${token('color.border', B100)};
	}
`;

type RightPanelDragHandleProps = {
	onResizeDrag: (offset: number) => void;
	onResizeComplete: () => void;
};

type PreviousUpdate = {
	clientX: number;
	time: number;
};

export const RightPanelDragHandle: FC<RightPanelDragHandleProps> = ({
	onResizeDrag,
	onResizeComplete,
}) => {
	const handleRef = useRef<HTMLDivElement>(null);
	const [isDown, setIsDown] = useState(false);
	const previousUpdate = useRef<PreviousUpdate>({ clientX: 0, time: 0 });

	useEffect(() => {
		const downListener = (e) => {
			if (e.button !== 0) {
				return;
			}

			setIsDown(true);
			previousUpdate.current = {
				clientX: e.clientX,
				time: Date.now(),
			};
			document.body.style.cursor = 'col-resize';
			e.preventDefault();
		};

		const handleRefCurrent = handleRef.current;

		handleRefCurrent?.addEventListener('mousedown', downListener);

		return () => {
			document.body.style.cursor = 'default';
			handleRefCurrent?.removeEventListener('mousedown', downListener);
		};
	}, []);

	useEffect(() => {
		const upListener = (e) => {
			if (e.button !== 0) {
				return;
			}

			setIsDown(false);
			document.body.style.cursor = 'default';
			onResizeComplete();
		};

		const moveListener = (e) => {
			if (e.button !== 0) {
				return;
			}

			if (isDown) {
				const now = Date.now();
				if (now - previousUpdate.current.time > 30) {
					onResizeDrag(e.clientX - previousUpdate.current.clientX);
					previousUpdate.current = {
						clientX: e.clientX,
						time: now,
					};
				}
				e.preventDefault();
			}
		};

		window.addEventListener('mouseup', upListener);
		window.addEventListener('mousemove', moveListener);

		return () => {
			window.removeEventListener('mouseup', upListener);
			window.removeEventListener('mousemove', moveListener);
		};
	}, [isDown, onResizeDrag, onResizeComplete]);

	return <DragHandle ref={handleRef} isDown={isDown} data-testid="resize-handle" />;
};
